import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $createParagraphNode, $getRoot } from 'lexical';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useEditorProps } from '../../core/context/EditorPropsContext';
import { useFeatureEnabled } from '../../core/context/FeaturesContext';
import { keepSelection, lineBoxClasses } from '../../ui/styles';

/**
 * `features.clearInput`: an X that empties the editor, the way `Input` offers one with `onClear`.
 * - The editor is uncontrolled, so it clears itself; the host hears about it through `onChange('')`
 *   (with multi-language, only the current language is emptied).
 * - The caret stays in the editor, so typing can start again straight away.
 */
export const ClearInputButton = () => {
  const [editor] = useLexicalComposerContext();
  const { disabled, size = 'm' } = useEditorProps();
  const enabled = useFeatureEnabled('clearInput');
  const { t } = useTranslation('common');

  if (!enabled || disabled) return null;

  const clear = () =>
    editor.update(() => {
      const paragraph = $createParagraphNode();
      $getRoot().clear().append(paragraph);
      paragraph.select();
    });

  return (
    <button
      type="button"
      title={t('editor.clear')}
      aria-label={t('editor.clear')}
      className={`text-text-2 flex shrink-0 cursor-pointer items-center self-end pr-2 hover:text-black ${lineBoxClasses[size]}`}
      onMouseDown={keepSelection}
      onClick={clear}
    >
      <X className="size-4" aria-hidden />
    </button>
  );
};
